import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper,
  Divider, Alert, Tooltip, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import WarningAmberIcon    from '@mui/icons-material/WarningAmber';
import SwapHorizIcon       from '@mui/icons-material/SwapHoriz';
import VisibilityIcon      from '@mui/icons-material/Visibility';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import BlockIcon           from '@mui/icons-material/Block';
import InfoOutlinedIcon    from '@mui/icons-material/InfoOutlined';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Normalise changed_fields — handles flat array (header-only) and structured object. */
function parseDupChangedFields(cf) {
  if (!cf) return { headerChanges: [], lineItemChanges: null };
  if (Array.isArray(cf)) return { headerChanges: cf, lineItemChanges: null };
  return {
    headerChanges:   cf.header_changes   || [],
    lineItemChanges: cf.line_item_changes || null,
  };
}

function hasAnyChange(cf) {
  const { headerChanges, lineItemChanges } = parseDupChangedFields(cf);
  return headerChanges.length > 0
    || (lineItemChanges && (
      (lineItemChanges.added   || []).length > 0 ||
      (lineItemChanges.removed || []).length > 0 ||
      (lineItemChanges.modified|| []).length > 0
    ));
}

/**
 * Map a backend `resolution` tag → the default user decision string.
 *   take_incoming → 'incoming'
 *   keep_existing → 'keep'
 *   fetch_br      → 'fetch_br'  (locked, not user-editable)
 *   user_choice   → 'incoming'  (default; user can toggle)
 */
function defaultDecisionForResolution(resolution) {
  switch (resolution) {
    case 'keep_existing': return 'keep';
    case 'fetch_br':      return 'fetch_br';
    case 'take_incoming':
    case 'user_choice':
    default:              return 'incoming';
  }
}

/**
 * Build the initial per-field decision map for one duplicate.
 * { [fieldName]: 'incoming' | 'keep' | 'fetch_br' }
 */
function buildInitialFieldDecisions(headerChanges) {
  const map = {};
  for (const cf of headerChanges) {
    map[cf.field] = defaultDecisionForResolution(cf.resolution);
  }
  return map;
}

/** Format an ISO timestamp from the backend into a readable local date+time string. */
function formatFetchedAt(isoString) {
  if (!isoString) return null;
  try {
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return isoString;
    return d.toLocaleString(undefined, {
      year:   'numeric', month:  'short',  day:    '2-digit',
      hour:   '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return isoString;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-field decision toggle
// ─────────────────────────────────────────────────────────────────────────────

function FieldDecisionToggle({ resolution, value, onChange, disabled }) {
  // fetch_br is always locked — no toggle
  if (resolution === 'fetch_br') {
    return (
      <Tooltip title="DC Date is always fetched fresh from Bamboo Rose — not user-editable">
        <Chip
          size="small"
          label="Fetched From BR Automatically"
          color="info"
          variant="outlined"
          sx={{ fontSize: '0.72rem', fontWeight: 700, cursor: 'default', opacity: disabled ? 0.45 : 1 }}
        />
      </Tooltip>
    );
  }

  return (
    <ToggleButtonGroup
      size="small"
      exclusive
      value={value}
      onChange={(_, v) => { if (v !== null && !disabled) onChange(v); }}
      sx={{ height: 28, opacity: disabled ? 0.4 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
    >
      <ToggleButton
        value="incoming"
        sx={{
          fontSize: '0.72rem', fontWeight: 700, px: 1.5, py: 0.5, lineHeight: 1.2,
          whiteSpace: 'nowrap',
          '&.Mui-selected': { bgcolor: 'rgba(245,158,11,0.18)', color: 'warning.dark', borderColor: 'warning.main' },
        }}
      >
        Take Incoming
      </ToggleButton>
      <ToggleButton
        value="keep"
        sx={{
          fontSize: '0.72rem', fontWeight: 700, px: 1.5, py: 0.5, lineHeight: 1.2,
          whiteSpace: 'nowrap',
          '&.Mui-selected': { bgcolor: 'rgba(16,185,129,0.18)', color: 'success.dark', borderColor: 'success.main' },
        }}
      >
        Keep Existing
      </ToggleButton>
    </ToggleButtonGroup>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main dialog
// ─────────────────────────────────────────────────────────────────────────────

/**
 * DuplicateDetectionDialog
 *
 * Props:
 *   open              – boolean
 *   duplicates        – array of duplicate descriptor objects
 *   onCancel          – () => void
 *   onOverride        – (filenamesToOverride: string[]) => void   [legacy full-override]
 *   onKeepAll         – () => void
 *   onFieldOverride   – ({ filenames, field_decisions }) => void  [new per-field path]
 */
export default function DuplicateDetectionDialog({
  open,
  duplicates = [],
  onCancel,
  onOverride,
  onKeepAll,
  onFieldOverride,
}) {
  const navigate = useNavigate();

  // Per-PO decision: { [index]: 'override' | 'keep' | null }
  const [decisions, setDecisions] = useState({});

  // Per-PO, per-field decisions: { [index]: { [fieldName]: 'incoming' | 'keep' | 'fetch_br' } }
  const [fieldDecisions, setFieldDecisions] = useState({});

  // Reset whenever the dialog opens with new duplicates
  useEffect(() => {
    if (!open) return;
    setDecisions({});
    const initial = {};
    duplicates.forEach((dup, idx) => {
      const { headerChanges, lineItemChanges } = parseDupChangedFields(dup.changed_fields);
      initial[idx] = buildInitialFieldDecisions(headerChanges);
      // Initialise per-row, per-field decisions for modified line item rows
      if (lineItemChanges && (lineItemChanges.modified || []).length > 0) {
        const liRows = {};
        lineItemChanges.modified.forEach(m => {
          liRows[String(m.row_number)] = {};
          (m.changes || []).forEach(c => {
            liRows[String(m.row_number)][c.field] = 'incoming'; // default: take incoming
          });
        });
        initial[idx]['__li_rows__'] = liRows;
      }
    });
    setFieldDecisions(initial);
  }, [open, duplicates]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || duplicates.length === 0) return null;

  // ── Exact-duplicate fast-path ─────────────────────────────────────────────
  const allExact = duplicates.every(d => !hasAnyChange(d.changed_fields));
  if (allExact) {
    return (
      <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CheckCircleIcon sx={{ color: 'info.main', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={700}>
              Complete Duplicate{duplicates.length > 1 ? 's' : ''} Detected
            </Typography>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2.5 }}>
          <Alert severity="info" sx={{ borderRadius: 2, mb: duplicates.length > 1 ? 2 : 0 }}>
            {duplicates.length === 1 ? (
              <>PO <strong>{duplicates[0].po_number}</strong> is an exact duplicate — no field differences from the existing record. Nothing has been changed.</>
            ) : (
              <>{duplicates.length} POs are exact duplicates — no field differences from the existing records. Nothing has been changed.</>
            )}
          </Alert>

          {duplicates.length > 1 && duplicates.map((dup, idx) => (
            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 15, color: 'info.main' }} />
              <Typography sx={{ fontSize: '0.85rem', fontFamily: 'monospace', color: 'primary.main', fontWeight: 600 }}>
                {dup.po_number}
              </Typography>
              {dup.filename && (
                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>— {dup.filename}</Typography>
              )}
            </Box>
          ))}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          {duplicates.some(d => d.existing_po_id) && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<VisibilityIcon />}
              onClick={() => {
                onCancel();
                const validDups = duplicates.filter(d => d.existing_po_id);
                const batch = validDups.map(d => d.existing_po_id);
                const poNumberMap = Object.fromEntries(
                  validDups.map(d => [String(d.existing_po_id), d.po_number || String(d.existing_po_id)])
                );
                navigate(`/audit/${batch[0]}`, { state: { processedPoIds: batch, poNumberMap } });
              }}
              sx={{ mr: 'auto' }}
            >
              View in Audit
            </Button>
          )}
          <Button variant="contained" color="primary" onClick={() => { if (onKeepAll) onKeepAll(); else onCancel(); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Mixed / changed duplicates — full per-field flow ──────────────────────

  const decidePO = (idx, choice) =>
    setDecisions(prev => ({ ...prev, [idx]: prev[idx] === choice ? null : choice }));

  const setFieldDecision = (dupIdx, fieldName, value) =>
    setFieldDecisions(prev => ({
      ...prev,
      [dupIdx]: { ...(prev[dupIdx] || {}), [fieldName]: value },
    }));

  /** Set decision for one field inside one modified line item row. */
  const setLiRowFieldDecision = (dupIdx, rowKey, fieldName, value) =>
    setFieldDecisions(prev => {
      const base    = prev[dupIdx] || {};
      const liRows  = base['__li_rows__'] || {};
      const rowMap  = liRows[rowKey] || {};
      return {
        ...prev,
        [dupIdx]: {
          ...base,
          '__li_rows__': { ...liRows, [rowKey]: { ...rowMap, [fieldName]: value } },
        },
      };
    });

  /** Bulk-set all fields in one modified row to the same decision. */
  const bulkSetLiRow = (dupIdx, rowKey, choice) =>
    setFieldDecisions(prev => {
      const base   = prev[dupIdx] || {};
      const liRows = base['__li_rows__'] || {};
      const rowMap = liRows[rowKey] || {};
      const updated = Object.fromEntries(Object.keys(rowMap).map(f => [f, choice]));
      return {
        ...prev,
        [dupIdx]: {
          ...base,
          '__li_rows__': { ...liRows, [rowKey]: updated },
        },
      };
    });

  /**
   * Bulk quick-set: set all user-editable fields for a specific PO to 'incoming' or 'keep'.
   * dc_date (fetch_br) is never overridden by this.
   */
  const bulkSetFields = (dupIdx, choice) => {
    const { headerChanges } = parseDupChangedFields(duplicates[dupIdx].changed_fields);
    setFieldDecisions(prev => {
      const updated = { ...(prev[dupIdx] || {}) };
      for (const cf of headerChanges) {
        if (cf.resolution !== 'fetch_br') {
          updated[cf.field] = choice;
        }
      }
      return { ...prev, [dupIdx]: updated };
    });
  };

  // A PO is considered decided if the user explicitly clicked Override/Keep,
  // OR if they've interacted with any field-level toggle (implies intent to override).
  const isPODecided = (idx) => {
    if (decisions[idx] != null) return true;
    // Any field toggled away from its default → treat as implicit override intent
    const perField = fieldDecisions[idx] || {};
    const { headerChanges } = parseDupChangedFields(duplicates[idx]?.changed_fields);
    const hasCustomFieldChoice = headerChanges.some(cf => {
      const def = defaultDecisionForResolution(cf.resolution);
      return perField[cf.field] != null && perField[cf.field] !== def;
    });
    const liRows = perField['__li_rows__'] || {};
    const hasCustomLiChoice = Object.values(liRows).some(rowMap =>
      Object.values(rowMap).some(v => v === 'keep')
    );
    return hasCustomFieldChoice || hasCustomLiChoice || perField['__line_items__'] === 'keep';
  };

  const allDecided    = duplicates.every((_, i) => isPODecided(i));
  // Effective decision: explicit click wins; implicit field changes = override
  const effectiveDecision = (idx) => decisions[idx] ?? (isPODecided(idx) ? 'override' : null);
  const anyOverride   = duplicates.some((_, i) => effectiveDecision(i) === 'override');
  const overrideDups  = duplicates.filter((_, i) => effectiveDecision(i) === 'override');
  const overrideFiles = overrideDups.map(d => d.filename).filter(Boolean);

  /**
   * Build a merged field_decisions map for all POs being overridden.
   * We merge across all PO field-decision maps — if any PO says 'keep' for a field,
   * the merged result respects that for the POs that have the field in their diff.
   *
   * For simplicity (and because the backend applies decisions per-PO via the old_values map),
   * we use the union of all field decisions. This is safe: the backend only patches fields
   * that are in the dup descriptor's changed_fields for the relevant PO.
   */
  const buildMergedFieldDecisions = () => {
    const merged   = {};
    const mergedLi = {}; // { [rowKey]: { [field]: decision } }
    duplicates.forEach((_, idx) => {
      if (effectiveDecision(idx) !== 'override') return;
      const perField = fieldDecisions[idx] || {};
      for (const [field, dec] of Object.entries(perField)) {
        if (field === '__li_rows__') continue; // handled separately below
        if (!merged[field] || dec === 'keep' || dec === 'fetch_br') {
          merged[field] = dec;
        }
      }
      // Merge per-row, per-field decisions
      const liRows = perField['__li_rows__'] || {};
      for (const [rowKey, rowFields] of Object.entries(liRows)) {
        if (!mergedLi[rowKey]) mergedLi[rowKey] = {};
        for (const [field, dec] of Object.entries(rowFields)) {
          if (!mergedLi[rowKey][field] || dec === 'keep') {
            mergedLi[rowKey][field] = dec;
          }
        }
      }
    });
    if (Object.keys(mergedLi).length > 0) merged['__li_rows__'] = mergedLi;
    return merged;
  };

  const handleApply = () => {
    if (!anyOverride) {
      if (onKeepAll) onKeepAll();
      else onCancel();
      return;
    }

    // Use per-field path if available
    if (onFieldOverride) {
      onFieldOverride({
        filenames:       overrideFiles,
        field_decisions: buildMergedFieldDecisions(),
      });
    } else {
      // Fallback: legacy full override
      onOverride(overrideFiles);
    }
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningAmberIcon sx={{ color: 'warning.main', fontSize: 28 }} />
          <Typography variant="h6" fontWeight={700}>
            Duplicate PO{duplicates.length > 1 ? 's' : ''} Detected
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2.5 }}>
        <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 2 }}>
          {duplicates.length === 1 ? (
            <>PO <strong>{duplicates[0].po_number}</strong> already exists in the database. </>
          ) : (
            <>{duplicates.length} POs already exist in the database. </>
          )}
          For each PO, choose to <strong>Override</strong> (bring in new data) or <strong>Keep Existing</strong>.
          When overriding, use the field-level toggles to control which values to take.
        </Alert>

        {duplicates.map((dup, idx) => {
          const decision = decisions[idx];
          const { headerChanges: dupHeaderChanges, lineItemChanges: dupLiChanges } = parseDupChangedFields(dup.changed_fields);
          const isExact   = !hasAnyChange(dup.changed_fields);
          const perField  = fieldDecisions[idx] || {};

          return (
            <Box
              key={idx}
              sx={{
                mb: idx < duplicates.length - 1 ? 3 : 0,
                p: 2,
                border: '1px solid',
                borderRadius: 2,
                borderColor:
                  decision === 'override' ? 'warning.main' :
                  decision === 'keep'     ? 'success.main' :
                  'divider',
                bgcolor:
                  decision === 'override' ? 'rgba(245,158,11,0.04)' :
                  decision === 'keep'     ? 'rgba(16,185,129,0.04)' :
                  'transparent',
                transition: 'all 0.15s',
              }}
            >
              {/* ── PO header row ── */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  PO&nbsp;
                  <Box component="span" sx={{ fontFamily: 'monospace', color: 'primary.main', fontSize: '0.95rem' }}>
                    {dup.po_number}
                  </Box>
                </Typography>
                <Chip size="small" label={`Rev: ${dup.version}`} color="warning" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 600 }} />
                {dup.filename && (
                  <Chip size="small" label={dup.filename} variant="outlined"
                    sx={{ fontFamily: 'monospace', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }} />
                )}
                {decision === 'override' && (
                  <Chip size="small" icon={<SwapHorizIcon />} label="Will override" color="warning" sx={{ ml: 'auto', fontWeight: 700 }} />
                )}
                {decision === 'keep' && (
                  <Chip size="small" icon={<CheckCircleIcon />} label="Keeping existing" color="success" sx={{ ml: 'auto', fontWeight: 700 }} />
                )}
              </Box>

              {/* ── Diff table or exact-match notice ── */}
              {isExact ? (
                <Alert severity="info" sx={{ borderRadius: 1.5, mb: 1.5, py: 0.5 }}>
                  Exact duplicate — no field differences from the existing record.
                </Alert>
              ) : (
                <>
                  {/* Header field changes with per-field decision toggles */}
                  {(() => {
                    // Determine which BR-only rows to always show (dc_date + factory)
                    // These appear even if they're not in the diff (no change detected in PDF)
                    const brRef = dup.br_reference || {};
                    const diffFieldNames = new Set(dupHeaderChanges.map(c => c.field));
                    const brAlwaysRows = [
                      {
                        field: 'dc_date',
                        label: 'DC Date',
                        resolution: 'fetch_br',
                        brValue: brRef.dc_date || null,
                      },
                      {
                        field: 'factory',
                        label: 'Factory',
                        resolution: 'keep_existing',
                        brValue: brRef.factory || null,
                      },
                    ].filter(r => !diffFieldNames.has(r.field)); // skip if already in diff

                    const showTable = dupHeaderChanges.length > 0 || brAlwaysRows.length > 0;
                    const fetchedAt = formatFetchedAt(brRef.fetched_at);

                    return showTable ? (
                      <>
                        {/* Bulk quick-set row — only when there are editable diff fields */}
                        {dupHeaderChanges.length > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, opacity: decision === 'keep' ? 0.4 : 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                              Quick-set all fields:
                            </Typography>
                            <Button size="small" variant="outlined" color="warning"
                              sx={{ fontSize: '0.68rem', py: 0, px: 1, height: 24 }}
                              disabled={decision === 'keep'}
                              onClick={() => bulkSetFields(idx, 'incoming')}>
                              Override All Fields
                            </Button>
                            <Button size="small" variant="outlined" color="success"
                              sx={{ fontSize: '0.68rem', py: 0, px: 1, height: 24 }}
                              disabled={decision === 'keep'}
                              onClick={() => bulkSetFields(idx, 'keep')}>
                              Keep All Fields
                            </Button>
                          </Box>
                        )}

                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, mb: 0.5 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700, width: '22%' }}>Field</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '28%', color: 'error.main' }}>Stored value</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '28%', color: 'success.main' }}>New value</TableCell>
                                <TableCell sx={{ fontWeight: 700, width: '22%', textAlign: 'center' }}>Decision</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {/* Regular diff rows */}
                              {dupHeaderChanges.map((cf, ci) => {
                                const fieldVal = perField[cf.field] ?? defaultDecisionForResolution(cf.resolution);
                                return (
                                  <TableRow
                                    key={`diff_${ci}`}
                                    sx={{
                                      '&:last-child td': { border: 0 },
                                      bgcolor:
                                        fieldVal === 'incoming'  ? 'rgba(245,158,11,0.04)' :
                                        fieldVal === 'keep'      ? 'rgba(16,185,129,0.04)' :
                                        fieldVal === 'fetch_br'  ? 'rgba(59,130,246,0.04)' :
                                        'transparent',
                                    }}
                                  >
                                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8rem' }}>
                                      {cf.label || cf.field}
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ bgcolor: 'rgba(239,68,68,0.15)', color: 'error.main', px: 1, py: 0.25, borderRadius: 1, display: 'inline-block', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                        {cf.old_value ?? <em style={{ opacity: 0.6 }}>empty</em>}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: 'success.main', px: 1, py: 0.25, borderRadius: 1, display: 'inline-block', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                        {cf.new_value ?? <em style={{ opacity: 0.6 }}>empty</em>}
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <FieldDecisionToggle
                                        resolution={cf.resolution}
                                        value={fieldVal}
                                        onChange={v => setFieldDecision(idx, cf.field, v)}
                                        disabled={decision === 'keep'}
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}

                              {/* Always-shown BR rows: DC Date + Factory (not already in diff) */}
                              {brAlwaysRows.map((br, bi) => {
                                const fieldVal = perField[br.field] ?? defaultDecisionForResolution(br.resolution);
                                return (
                                  <TableRow
                                    key={`br_${bi}`}
                                    sx={{
                                      bgcolor: 'rgba(59,130,246,0.04)',
                                      '&:last-child td': { border: 0 },
                                    }}
                                  >
                                    <TableCell sx={{ fontWeight: 600, color: 'info.main', fontSize: '0.8rem' }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {br.label}
                                        <Chip size="small" label="BR" color="info" variant="outlined"
                                          sx={{ fontSize: '0.62rem', height: 16, px: 0, fontWeight: 700 }} />
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ color: 'text.disabled', fontSize: '0.8rem', fontStyle: 'italic' }}>
                                        unchanged
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                        <Box sx={{ bgcolor: 'rgba(59,130,246,0.15)', color: 'info.main', px: 1, py: 0.25, borderRadius: 1, display: 'inline-block', fontSize: '0.8rem', fontFamily: 'monospace' }}>
                                          {br.brValue ?? <em style={{ opacity: 0.6 }}>not found in BR</em>}
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'info.main', fontSize: '0.7rem', opacity: 0.75 }}>
                                          from BR
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <FieldDecisionToggle
                                        resolution={br.resolution}
                                        value={fieldVal}
                                        onChange={v => setFieldDecision(idx, br.field, v)}
                                        disabled={decision === 'keep'}
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        {/* BR last-fetched timestamp caption */}
                        {fetchedAt && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.72rem' }}>
                              BR data last fetched:&nbsp;<strong style={{ fontWeight: 600 }}>{fetchedAt}</strong>
                              &nbsp;— DC Date is always fetched fresh from Bamboo Rose when saving
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : null;
                  })()}

                  {/* Line item changes — with a section-level keep/override toggle */}
                  {dupLiChanges && (
                    <>
                      {/* Section-level decision for all line items */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, p: 1.25, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider', bgcolor: 'transparent' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
                          Line Items:
                        </Typography>
                        <ToggleButtonGroup
                          size="small"
                          exclusive
                          value={perField['__line_items__'] ?? 'incoming'}
                          onChange={(_, v) => { if (v !== null && decision !== 'keep') setFieldDecision(idx, '__line_items__', v); }}
                          sx={{ height: 28, opacity: decision === 'keep' ? 0.4 : 1, pointerEvents: decision === 'keep' ? 'none' : 'auto' }}
                        >
                          <ToggleButton value="incoming" sx={{ fontSize: '0.72rem', fontWeight: 700, px: 1.5, py: 0.5, whiteSpace: 'nowrap', '&.Mui-selected': { bgcolor: 'rgba(245,158,11,0.18)', color: 'warning.dark', borderColor: 'warning.main' } }}>
                            Take Incoming Rows
                          </ToggleButton>
                          <ToggleButton value="keep" sx={{ fontSize: '0.72rem', fontWeight: 700, px: 1.5, py: 0.5, whiteSpace: 'nowrap', '&.Mui-selected': { bgcolor: 'rgba(16,185,129,0.18)', color: 'success.dark', borderColor: 'success.main' } }}>
                            Keep Existing Rows
                          </ToggleButton>
                        </ToggleButtonGroup>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                          {(perField['__line_items__'] ?? 'incoming') === 'keep'
                            ? 'Existing line item rows will be kept as-is'
                            : 'All line item rows from the incoming PDF will replace existing ones'}
                        </Typography>
                      </Box>

                      {(dupLiChanges.modified || []).length > 0 && (
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'warning.main', letterSpacing: '0.05em', display: 'block', mb: 0.5 }}>
                            LINE ITEM CHANGES — {dupLiChanges.modified.length} row{dupLiChanges.modified.length !== 1 ? 's' : ''} modified
                          </Typography>
                          {dupLiChanges.modified.map((m, mi) => {
                            const rowKey       = String(m.row_number);
                            const liRowDecisions = ((perField['__li_rows__'] || {})[rowKey]) || {};
                            const sectionIsKeep  = (perField['__line_items__'] ?? 'incoming') === 'keep';
                            const rowDisabled    = decision === 'keep' || sectionIsKeep;
                            const rowLabel = rowKey.startsWith('__idx_')
                              ? `Row ${parseInt(rowKey.replace('__idx_', ''), 10) + 1}`
                              : `Row ${rowKey}`;
                            return (
                            <Box key={mi} sx={{ mb: 1.5, pl: 1, borderLeft: '2px solid', borderColor: 'warning.main' }}>
                              {/* Row header + bulk quick-set */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', fontWeight: 700 }}>
                                  {rowLabel}
                                </Typography>
                                {!rowDisabled && (
                                  <>
                                    <Button size="small" variant="outlined" color="warning"
                                      sx={{ fontSize: '0.62rem', py: 0, px: 0.75, height: 20, minWidth: 0 }}
                                      onClick={() => bulkSetLiRow(idx, rowKey, 'incoming')}>
                                      Override All
                                    </Button>
                                    <Button size="small" variant="outlined" color="success"
                                      sx={{ fontSize: '0.62rem', py: 0, px: 0.75, height: 20, minWidth: 0 }}
                                      onClick={() => bulkSetLiRow(idx, rowKey, 'keep')}>
                                      Keep All
                                    </Button>
                                  </>
                                )}
                              </Box>
                              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', width: '22%' }}>Field</TableCell>
                                      <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', width: '26%', color: 'error.main' }}>Stored</TableCell>
                                      <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', width: '26%', color: 'success.main' }}>Incoming</TableCell>
                                      <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', width: '26%', textAlign: 'center' }}>Decision</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(m.changes || []).map((c, ci) => {
                                      const fieldDec = liRowDecisions[c.field] ?? 'incoming';
                                      return (
                                      <TableRow key={ci} sx={{
                                        '&:last-child td': { border: 0 },
                                        bgcolor: rowDisabled ? 'transparent' :
                                          fieldDec === 'incoming' ? 'rgba(245,158,11,0.04)' : 'rgba(16,185,129,0.04)',
                                      }}>
                                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>{c.label || c.field}</TableCell>
                                        <TableCell>
                                          <Box sx={{ bgcolor: 'rgba(239,68,68,0.15)', color: 'error.main', px: 0.75, py: 0.2, borderRadius: 1, display: 'inline-block', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                            {c.old_value ?? <em style={{ opacity: 0.6 }}>empty</em>}
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Box sx={{ bgcolor: 'rgba(16,185,129,0.15)', color: 'success.main', px: 0.75, py: 0.2, borderRadius: 1, display: 'inline-block', fontSize: '0.75rem', fontFamily: 'monospace' }}>
                                            {c.new_value ?? <em style={{ opacity: 0.6 }}>empty</em>}
                                          </Box>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                          <ToggleButtonGroup
                                            size="small"
                                            exclusive
                                            value={fieldDec}
                                            onChange={(_, v) => { if (v !== null && !rowDisabled) setLiRowFieldDecision(idx, rowKey, c.field, v); }}
                                            sx={{ height: 24, opacity: rowDisabled ? 0.35 : 1, pointerEvents: rowDisabled ? 'none' : 'auto' }}
                                          >
                                            <ToggleButton value="incoming" sx={{ fontSize: '0.65rem', fontWeight: 700, px: 1, py: 0, whiteSpace: 'nowrap', '&.Mui-selected': { bgcolor: 'rgba(245,158,11,0.18)', color: 'warning.dark', borderColor: 'warning.main' } }}>
                                              Incoming
                                            </ToggleButton>
                                            <ToggleButton value="keep" sx={{ fontSize: '0.65rem', fontWeight: 700, px: 1, py: 0, whiteSpace: 'nowrap', '&.Mui-selected': { bgcolor: 'rgba(16,185,129,0.18)', color: 'success.dark', borderColor: 'success.main' } }}>
                                              Keep
                                            </ToggleButton>
                                          </ToggleButtonGroup>
                                        </TableCell>
                                      </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                            );
                          })}
                        </Box>
                      )}
                      {(dupLiChanges.added || []).length > 0 && (
                        <Alert severity="success" sx={{ borderRadius: 1.5, mb: 1, py: 0.5, fontSize: '0.8rem' }}>
                          {dupLiChanges.added.length} line item row{dupLiChanges.added.length !== 1 ? 's' : ''} added in incoming PDF
                        </Alert>
                      )}
                      {(dupLiChanges.removed || []).length > 0 && (
                        <Alert severity="error" sx={{ borderRadius: 1.5, mb: 1, py: 0.5, fontSize: '0.8rem' }}>
                          {dupLiChanges.removed.length} line item row{dupLiChanges.removed.length !== 1 ? 's' : ''} removed in incoming PDF
                        </Alert>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ── Per-PO action buttons (Override & Save / Keep Existing) ── */}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Tooltip title="Replace the stored PO with the incoming PDF data (use field toggles above to control which fields)">
                  <Button
                    size="small"
                    variant={decision === 'override' ? 'contained' : 'outlined'}
                    color="warning"
                    startIcon={<SwapHorizIcon />}
                    onClick={() => decidePO(idx, 'override')}
                    sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                  >
                    Override & Save
                  </Button>
                </Tooltip>
                <Tooltip title="Keep the existing database record entirely unchanged">
                  <Button
                    size="small"
                    variant={decision === 'keep' ? 'contained' : 'outlined'}
                    color="success"
                    startIcon={<BlockIcon />}
                    onClick={() => decidePO(idx, 'keep')}
                    sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                  >
                    Keep Existing
                  </Button>
                </Tooltip>
                {dup.existing_po_id && (
                  <Tooltip title="Open this PO in the audit screen">
                    <Button
                      size="small"
                      variant="text"
                      color="primary"
                      startIcon={<VisibilityIcon />}
                      onClick={() => { onCancel(); navigate(`/audit/${dup.existing_po_id}`); }}
                      sx={{ fontSize: '0.75rem', ml: 'auto' }}
                    >
                      View in Audit
                    </Button>
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
      </DialogContent>

      <Divider />

      {/* ── Bottom action bar ── */}
      <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* View all in audit — left side */}
        {duplicates.some(d => d.existing_po_id) && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<VisibilityIcon />}
            onClick={() => {
              onCancel();
              const validDups = duplicates.filter(d => d.existing_po_id);
              const batch = validDups.map(d => d.existing_po_id);
              const poNumberMap = Object.fromEntries(
                validDups.map(d => [String(d.existing_po_id), d.po_number || String(d.existing_po_id)])
              );
              navigate(`/audit/${batch[0]}`, { state: { processedPoIds: batch, poNumberMap } });
            }}
            sx={{ mr: 'auto' }}
          >
            View All in Audit
          </Button>
        )}

        {/* Bulk quick-set all POs */}
        {duplicates.length > 1 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              sx={{ fontSize: '0.72rem', fontWeight: 700 }}
              onClick={() => {
                const next = {};
                duplicates.forEach((_, i) => { next[i] = 'override'; });
                setDecisions(next);
              }}
            >
              Override All POs
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="success"
              sx={{ fontSize: '0.72rem', fontWeight: 700 }}
              onClick={() => {
                const next = {};
                duplicates.forEach((_, i) => { next[i] = 'keep'; });
                setDecisions(next);
              }}
            >
              Keep All POs
            </Button>
          </Box>
        )}

        <Button variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>

        <Tooltip title={!allDecided ? 'Choose Override or Keep for every PO above' : ''}>
          <span>
            <Button
              variant="contained"
              color={anyOverride ? 'warning' : 'success'}
              disabled={!allDecided}
              onClick={handleApply}
              startIcon={anyOverride ? <SwapHorizIcon /> : <CheckCircleIcon />}
              sx={{ fontWeight: 700 }}
            >
              {!allDecided
                ? `Decide all (${Object.keys(decisions).length}/${duplicates.length})`
                : anyOverride
                  ? `Apply — Override ${overrideFiles.length}, Keep ${duplicates.length - overrideFiles.length}`
                  : 'Keep All Existing'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
